// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import {
  Button, Container, Dropdown, Header, Icon, Label, Loader, Message, Table,
} from 'semantic-ui-react';
import { NewBusinessTemplateEditor } from '@loa/new-business-form';
import { providerApi, type Provider } from '../api/providerApi';
import { policyTypeApi, type PolicyType } from '../api/policyTypeApi';
import {
  newBusinessTemplateApi, newBusinessTemplateEditorApi, type NewBusinessTemplate,
} from '../api/newBusinessTemplateApi';

/**
 * Platform-admin page for New Business Application templates: per-provider,
 * per-product blank provider forms + coordinate-overlay bindings + parameter
 * schema. Mirrors the Quote Terms admin page. A template is created as a draft,
 * then a PDF is uploaded, bindings placed (≥1 SIGNATURE) and the parameter
 * schema authored before it can be activated. The authoring editor is the shared
 * {@link NewBusinessTemplateEditor} (also used by the Advisor UI), with the JSON
 * editors enabled here for platform admins.
 */
export default function NewBusinessTemplatesPage() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [providerId, setProviderId] = useState('');
  const [products, setProducts] = useState<PolicyType[]>([]);
  const [templates, setTemplates] = useState<NewBusinessTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<NewBusinessTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [newProductId, setNewProductId] = useState('');

  useEffect(() => {
    providerApi.getAll({ isActive: true, size: 1000 })
      .then((d) => setProviders(d.content))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load providers'));
  }, []);

  const loadForProvider = useCallback((pid: string) => {
    setLoading(true);
    setError(null);
    Promise.all([
      newBusinessTemplateApi.list(pid),
      policyTypeApi.getAll({ providerId: pid, isActive: true, size: 1000 }),
    ])
      .then(([list, productPage]) => {
        setTemplates(list);
        setProducts(productPage.content);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load templates'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (providerId) loadForProvider(providerId);
  }, [providerId, loadForProvider]);

  const createDraft = async () => {
    if (!newProductId) return;
    setSaving(true);
    try {
      // template-service owns the registry and can't resolve provider/product names,
      // so the caller denormalises the display fields + category onto the template.
      const product = products.find((p) => p.id === newProductId);
      const provider = providers.find((p) => p.id === providerId);
      const created = await newBusinessTemplateApi.create({
        providerId, productId: newProductId, currencyCode: 'EUR',
        category: product?.category, productName: product?.name,
        providerName: provider?.name, providerLogoUrl: provider?.logoUrl,
      });
      setNewProductId('');
      loadForProvider(providerId);
      setEditing(created);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t: NewBusinessTemplate) => {
    if (!t.id) return;
    try {
      await (t.isActive ? newBusinessTemplateApi.deactivate(t.id) : newBusinessTemplateApi.activate(t.id));
      loadForProvider(providerId);
    } catch (e) {
      // The publish gate (≥1 signature + PDF) surfaces here as a 422.
      setError(e instanceof Error ? e.message : 'Could not change status');
    }
  };

  const removeTemplate = async (t: NewBusinessTemplate) => {
    if (!t.id) return;
    await newBusinessTemplateApi.remove(t.id);
    if (editing?.id === t.id) setEditing(null);
    loadForProvider(providerId);
  };

  const providerOptions = [
    { key: 'none', text: 'Select a provider…', value: '' },
    ...providers.map((p) => ({ key: p.id!, text: p.name, value: p.id! })),
  ];
  const productOptions = products
    .filter((p) => !templates.some((t) => t.productId === p.id))
    .map((p) => ({ key: p.id!, text: p.name, value: p.id! }));

  return (
    <Container style={{ marginTop: 24 }}>
      <Header as="h2">New Business Templates</Header>
      <p style={{ color: '#6b7280' }}>
        Per-provider, per-product application forms. Upload the provider PDF, place data bindings
        (including at least one signature region), author the parameter schema, then activate.
      </p>

      <Dropdown
        selection search placeholder="Select a provider…"
        options={providerOptions} value={providerId}
        onChange={(_, d) => { setProviderId(String(d.value)); setEditing(null); }}
        data-testid="nb-provider-select"
      />

      {error && <Message error onDismiss={() => setError(null)} content={error} />}
      {loading && <Loader active inline style={{ marginLeft: 12 }} />}

      {providerId && (
        <>
          <div style={{ margin: '16px 0', display: 'flex', gap: 8, alignItems: 'center' }}>
            <Dropdown
              selection placeholder="Add template for product…"
              options={productOptions} value={newProductId}
              disabled={productOptions.length === 0}
              onChange={(_, d) => setNewProductId(String(d.value))}
              data-testid="nb-new-product-select"
            />
            <Button primary disabled={!newProductId || saving} onClick={createDraft} data-testid="nb-add-template">
              <Icon name="plus" /> Add template
            </Button>
          </div>

          <Table celled striped data-testid="nb-templates-table">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Product</Table.HeaderCell>
                <Table.HeaderCell>Category</Table.HeaderCell>
                <Table.HeaderCell>PDF</Table.HeaderCell>
                <Table.HeaderCell>Signature regions</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {templates.map((t) => (
                <Table.Row key={t.id} active={editing?.id === t.id}>
                  <Table.Cell>{t.productName ?? t.productId}</Table.Cell>
                  <Table.Cell>{t.category}</Table.Cell>
                  <Table.Cell>{t.hasPdf ? <Icon name="check" color="green" /> : '—'}</Table.Cell>
                  <Table.Cell>{t.signatureRegionCount ?? 0}</Table.Cell>
                  <Table.Cell>
                    <Label color={t.isActive ? 'green' : 'grey'} size="small">
                      {t.isActive ? 'Active' : 'Draft'}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    <Button.Group size="small">
                      <Button icon onClick={() => setEditing(t)} data-testid={`nb-edit-${t.id}`}>
                        <Icon name="edit" />
                      </Button>
                      <Button icon onClick={() => toggleActive(t)} data-testid={`nb-toggle-${t.id}`}>
                        <Icon name={t.isActive ? 'pause' : 'play'} />
                      </Button>
                      <Button icon negative onClick={() => removeTemplate(t)} data-testid={`nb-delete-${t.id}`}>
                        <Icon name="trash" />
                      </Button>
                    </Button.Group>
                  </Table.Cell>
                </Table.Row>
              ))}
              {templates.length === 0 && !loading && (
                <Table.Row><Table.Cell colSpan={6}>No templates yet for this provider.</Table.Cell></Table.Row>
              )}
            </Table.Body>
          </Table>
        </>
      )}

      {editing && (
        <div style={{ marginTop: 24, borderTop: '2px solid #e5e7eb', paddingTop: 16 }}>
          <NewBusinessTemplateEditor
            template={editing}
            api={newBusinessTemplateEditorApi}
            showJsonEditors
            onSaved={() => loadForProvider(providerId)}
            onClose={() => setEditing(null)}
          />
        </div>
      )}
    </Container>
  );
}
